import React, { useState } from 'react';
import { optimizeImage } from '../lib/image';

export default function FaqAccordion({
  items = [],
  title = 'Ofte stillede spørgsmål',
  subtitle = 'Få svar på de mest almindelige spørgsmål om terapi, hypnose og samtaler',
}) {
  const [openIndex, setOpenIndex] = useState(0);

  if (!items || items.length === 0) return null;

  return (
    <section className="bg-[#ECEEF1]">
      {/* Top Hero Image Block with large display heading */}
      <div className="relative overflow-hidden bg-[#171C21] pt-16 pb-28 sm:pt-20 sm:pb-32 px-4 sm:px-6 lg:px-8 text-center">
        {/* Ambient background photo */}
        <div className="absolute inset-0 z-0 opacity-25">
          <img
            src={optimizeImage(
              'https://assets.auxodev.dk/assets/ece5dc05-bafb-437f-b627-764ef307b8ad/img-4513.jpg',
              1200
            )}
            alt="Kims rolige samtalerum på Amager"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#171C21] via-[#171C21]/80 to-[#171C21]/60 z-0" />

        <div className="relative z-10 max-w-3xl mx-auto">
          <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/10 text-white/90 text-xs font-semibold uppercase tracking-wider mb-4 border border-white/15 backdrop-blur-xs">
            <i className="fa-solid fa-circle-question text-[11px] text-[#D9DDE2]" aria-hidden="true" />
            Klarhed & Svar
          </span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-white tracking-tight">
            {title}
          </h2>
          {subtitle && (
            <p className="mt-4 text-sm sm:text-base text-[#D9DDE2]/90 max-w-xl mx-auto leading-relaxed">
              {subtitle}
            </p>
          )}
        </div>
      </div>

      {/* Constrained accordion stack, topmost item overlaps boundary creating visual bridge */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 sm:pb-24">
        <div className="-mt-14 sm:-mt-16 relative z-20 space-y-3.5">
          {items.map((item, idx) => {
            const isOpen = openIndex === idx;
            const question = item.spoergsmaal || item.q || '';
            const answer = item.svar || item.a || '';

            return (
              <div
                key={idx}
                style={{ borderRadius: '10px' }}
                className="bg-white border border-[#D9DDE2] shadow-sm hover:shadow-md transition-all overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() => setOpenIndex(isOpen ? -1 : idx)}
                  aria-expanded={isOpen}
                  style={{ borderRadius: isOpen ? '10px 10px 0 0' : '10px' }}
                  className="w-full text-left px-6 sm:px-8 py-4 sm:py-5 flex items-center justify-between gap-4 cursor-pointer hover:bg-[#F1F4F8]/70 transition-colors"
                >
                  <span className="font-display font-medium text-base sm:text-lg text-[#11161d] leading-snug">
                    {question}
                  </span>
                  <div
                    className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 transition-transform duration-200 ${
                      isOpen
                        ? 'rotate-180 bg-[#5A232E] text-white shadow-xs'
                        : 'bg-[#ECEEF1] text-[#5A232E]'
                    }`}
                  >
                    <i className="fa-solid fa-chevron-down text-xs" aria-hidden="true" />
                  </div>
                </button>

                {isOpen && (
                  <div 
                    style={{ borderRadius: '0 0 10px 10px' }}
                    className="px-6 sm:px-8 pb-6 pt-1 text-sm text-[#11161d] leading-relaxed border-t border-[#D9DDE2]/50 animate-in fade-in duration-150 bg-white"
                  >
                    {typeof answer === 'string' && answer.startsWith('<') ? (
                      <div
                        className="prose prose-sm max-w-none text-[#6c7480]"
                        dangerouslySetInnerHTML={{ __html: answer }}
                      />
                    ) : (
                      <p className="text-[#6c7480]">{answer}</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
